import { Accordion } from '@delmonicos/shared-design-system-react/src/components/accordion/accordion';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { ButtonColor } from '@delmonicos/shared-design-system-react/src/components/button/button';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import {
  Icon,
  IconName,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Text } from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import classNames from 'classnames';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { GroupEnum } from '@model/routing-configuration.model';

import { MenuLink } from './menu-link';
import * as css from './menu.module.scss';

export interface MenuItem {
  label: string;
  icon: IconName;
  to: string;
  description?: string;
}

export interface GroupedMenuItem {
  group: GroupEnum;
  items: MenuItem[];
}

interface MenuProps {
  groupedMenuItems: GroupedMenuItem[];
  isSmall: boolean;
  setIsSmall: (isSmall: boolean) => void;
}

export const Menu = ({
  groupedMenuItems,
  isSmall,
  setIsSmall,
}: MenuProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const isActive = (to: string): boolean => {
    return pathname === to || pathname.startsWith(`${to}/`);
  };

  const supportGroup = groupedMenuItems.find(
    (group) => group.group === GroupEnum.SUPPORT,
  );

  const itemsWithoutSupport = groupedMenuItems.filter(
    (group) => group.group !== GroupEnum.SUPPORT,
  );

  return (
    <nav className={css.menuItems}>
      <Stack
        gap={isSmall ? GapEnum.XXS : GapEnum.M}
        direction="column"
        flex={1}>
        {!isSmall &&
          itemsWithoutSupport.map(({ group, items }, index) => (
            <Fragment key={group}>
              <Accordion
                titleIconOpen="minus"
                titleIconClose="plus"
                title={
                  <Text className={css.groupTitle}>
                    {t(`menu.group.${group}`)}
                  </Text>
                }>
                {items.map((item) => (
                  <MenuLink
                    key={item.to}
                    active={isActive(item.to)}
                    {...item}
                  />
                ))}
              </Accordion>
              {index < groupedMenuItems.length - 2 && <Divider />}
            </Fragment>
          ))}

        {isSmall &&
          itemsWithoutSupport.map(({ group, items }, index) => (
            <Fragment key={group}>
              {items.map((item) => (
                <MenuLink
                  isSmall={isSmall}
                  key={item.to}
                  active={isActive(item.to)}
                  {...item}
                />
              ))}
              {index < groupedMenuItems.length - 2 && <Divider />}
            </Fragment>
          ))}
      </Stack>

      {supportGroup && (
        <Stack
          gap={GapEnum.XS}
          direction="column"
          className={classNames(
            css.supportGroup,
            isSmall ? css.minifiedSupportGroup : css.fullSupportGroup,
          )}>
          {!isSmall && (
            <Text className={css.groupTitle}>
              {t(`menu.group.${supportGroup.group}`)}
            </Text>
          )}
          {supportGroup.items.map((item) => (
            <MenuLink
              isSmall={isSmall}
              key={item.to}
              active={isActive(item.to)}
              {...item}
            />
          ))}
        </Stack>
      )}

      {isSmall ? (
        <Stack justifyContent="flex-end" alignItems="center">
          <ButtonIcon
            icon="chevron-right"
            onClick={() => setIsSmall(!isSmall)}
            color={ButtonColor.Ghost}
          />
        </Stack>
      ) : (
        <button onClick={() => setIsSmall(!isSmall)}>
          <Stack justifyContent="space-between" alignItems="center">
            <Text>{t('menu.large')}</Text>
            <Icon name="chevron-left" />
          </Stack>
        </button>
      )}
    </nav>
  );
};
